import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2FormGrid,
  V2FormField,
  V2ConfirmDialog,
  LoadingState,
  StatePanel,
  Icon,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import type {
  FulfillmentStatus,
  SalesInvoiceListResponse,
  SalesInvoiceStatus,
  SalesOrder,
  SalesOrderFulfillment,
} from '../../lib/types/crm.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { RelatedOpenItemsCard } from '../finance/RelatedOpenItemsCard.js';
import { InvoiceIssueDialog } from './InvoiceIssueDialog.js';

interface AllocationDraft {
  readonly key: string;
  lineId: string;
  warehouseCode: string;
  locationId: string;
  locationCode: string;
  quantity: string;
}

interface StockBalanceListResponse {
  readonly balances: readonly {
    readonly productId: string;
    readonly warehouseId: string;
    readonly warehouseLocationId: string;
    readonly available: { readonly numerator: string; readonly denominator?: string } | string;
  }[];
}

const STATUS_TONE: Record<string, V2BadgeTone> = {
  پیش‌نویس: 'neutral',
  'تأیید شده': 'brand',
  'تحویل جزئی': 'warn',
  'تحویل کامل': 'success',
  'لغو شده': 'danger',
};

const FULFILLMENT_LABEL: Record<FulfillmentStatus, string> = {
  unreserved: 'رزرو نشده',
  partially_reserved: 'رزرو جزئی',
  ready_to_deliver: 'آماده تحویل',
  partially_delivered: 'تحویل جزئی',
  fully_delivered: 'تحویل کامل',
};

const FULFILLMENT_TONE: Record<FulfillmentStatus, V2BadgeTone> = {
  unreserved: 'neutral',
  partially_reserved: 'warn',
  ready_to_deliver: 'brand',
  partially_delivered: 'warn',
  fully_delivered: 'success',
};

const INVOICE_STATUS_LABEL: Record<SalesInvoiceStatus, string> = {
  draft: 'پیش‌نویس',
  submitted: 'ارسال‌شده برای تأیید',
  approved: 'تأییدشده',
  posted: 'ثبت قطعی',
  reversed: 'برگشت‌خورده',
};

const INVOICE_STATUS_TONE: Record<SalesInvoiceStatus, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'warn',
  approved: 'info',
  posted: 'success',
  reversed: 'danger',
};

function ErrorState({ status, title }: { readonly status: number; readonly title: string }) {
  return (
    <StatePanel
      variant={
        status === 403 ? 'permission_denied' : status === 404 ? 'not_found' : 'network_error'
      }
      title={title}
    />
  );
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Sales CRM.dc.html §06 SALES ORDERS — detail
 * status: KIT_CONFORMANT — line items are real and rendered (see
 *   QuoteDetailPage's own doc comment for the same ERP-224 history). The
 *   inventory-derived fulfillment status (ERP-157) is real and server-
 *   authoritative. Related AR open items are composed from GET
 *   .../finance/ar/open-items filtered by this order's ref (sales_handoff
 *   sourceReference). A printable sales-order document is available via the
 *   "چاپ سفارش فروش" link (routes/print/OrderPrintPage.tsx).
 *
 * BUG-08 reversal: ERP-157 shipped this page read-only on purpose
 * ("Reservation, delivery, and accounting mutations intentionally have no
 * UI controls") while it built the real inventory-derived status reporting
 * below. This QA pass names that gap a P0/P1 contract violation — the
 * ERP-270 makes confirmation allocate and reserve every line atomically,
 * and keeps active reservations selectable for delivery after refresh.
 * Financial invoice/AR/GL lifecycle actions remain read-only here.
 * state reference: ready · permission_denied · not_found · network_error
 * ERP-160 adds the sales-invoice/receivable projection; «صدور فاکتور»
 * (InvoiceIssueDialog) creates a delivery-backed DRAFT invoice. Submit, approve,
 * post and reversal stay on the invoice/finance screens (maker–checker).
 */
export function OrderDetailPage() {
  const { unit, label } = useAmountDisplay();
  const { slug, ref } = useParams<{ slug: string; ref: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [issuing, setIssuing] = useState(searchParams.get('issue') === 'invoice');
  const [confirming, setConfirming] = useState<'confirm' | 'cancel' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [allocations, setAllocations] = useState<AllocationDraft[]>([]);
  const [allocationDate, setAllocationDate] = useState('');

  // Deliver step
  const [selectedReservationId, setSelectedReservationId] = useState('');
  const [deliveryQuantity, setDeliveryQuantity] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [delivering, setDelivering] = useState(false);
  const [deliverError, setDeliverError] = useState<string | null>(null);
  const [lastMovementId, setLastMovementId] = useState<string | null>(null);

  // Project linkage
  const [creatingProject, setCreatingProject] = useState(false);
  const [createdProject, setCreatedProject] = useState<{
    projectRef: string;
    projectName: string;
  } | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);

  const order = useApiGet<SalesOrder>(slug && ref ? `${base}/api/v1/sales/orders/${ref}` : null, [
    slug,
    ref,
    refreshKey,
  ]);
  const fulfillment = useApiGet<SalesOrderFulfillment>(
    slug && ref ? `${base}/api/v1/sales/orders/${ref}/fulfillment` : null,
    [slug, ref, refreshKey],
  );
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?status=active&limit=200` : null,
    [slug],
  );
  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?status=active&limit=200` : null,
    [slug],
  );
  const locations = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/locations?status=active&limit=200` : null,
    [slug],
  );
  const stockBalances = useApiGet<StockBalanceListResponse>(
    slug ? `${base}/api/v1/inventory/stock-balances?limit=100` : null,
    [slug, refreshKey],
  );
  const invoices = useApiGet<SalesInvoiceListResponse>(
    slug && ref ? `${base}/api/v1/sales/orders/${ref}/invoices` : null,
    [slug, ref],
  );

  if (!slug || !ref) return null;

  async function runAction(action: 'confirm' | 'cancel') {
    setSubmitting(true);
    setActionError(null);
    try {
      if (action === 'confirm') {
        const payload = allocations.map((allocation) => {
          const warehouseId = warehouseIdByCode.get(allocation.warehouseCode);
          if (
            !warehouseId ||
            !allocation.locationId ||
            !allocation.lineId ||
            !allocation.quantity
          ) {
            throw new Error('ALLOCATION_INCOMPLETE');
          }
          return {
            orderLineId: allocation.lineId,
            warehouseId,
            warehouseLocationId: allocation.locationId,
            quantityNumerator: allocation.quantity,
          };
        });
        if (!payload.length || !allocationDate) throw new Error('ALLOCATION_INCOMPLETE');
        await apiPost(`${base}/api/v1/sales/orders/${ref}/confirm`, {
          allocations: payload,
          businessDate: allocationDate,
        });
      } else {
        await apiPost(`${base}/api/v1/sales/orders/${ref}/cancel`);
      }
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? err.envelope.error.message
          : err instanceof Error && err.message === 'ALLOCATION_INCOMPLETE'
            ? 'تخصیص انبار، محل، مقدار و تاریخ برای تأیید الزامی است.'
            : 'ثبت اقدام ناموفق بود.',
      );
    } finally {
      setSubmitting(false);
      setConfirming(null);
    }
  }

  const productIdByRef = new Map(
    (products.status === 'ready' ? products.data.items : []).map((p) => [
      String(p['code']),
      String(p['productId']),
    ]),
  );
  const warehouseOptions = warehouses.status === 'ready' ? warehouses.data.items : [];
  const warehouseIdByCode = new Map(
    warehouseOptions.map((w) => [String(w['code']), String(w['warehouseId'])]),
  );
  async function handleCreateProject() {
    setCreatingProject(true);
    setProjectError(null);
    try {
      const res = await apiPost<{ projectRef: string; projectName: string; replayed: boolean }>(
        `${base}/api/v1/sales/orders/${ref}/create-project`,
        {},
      );
      setCreatedProject({ projectRef: res.projectRef, projectName: res.projectName });
    } catch (err) {
      if (err instanceof ApiError) setProjectError(err.message);
      else setProjectError('خطا در ایجاد پروژه برای سفارش.');
    } finally {
      setCreatingProject(false);
    }
  }

  const locationOptions = locations.status === 'ready' ? locations.data.items : [];
  function updateAllocation(key: string, patch: Partial<AllocationDraft>) {
    setAllocations((current) =>
      current.map((allocation) =>
        allocation.key === key ? { ...allocation, ...patch } : allocation,
      ),
    );
  }

  function addAllocation(lineId: string) {
    setAllocations((current) => [
      ...current,
      {
        key: crypto.randomUUID(),
        lineId,
        warehouseCode: '',
        locationId: '',
        locationCode: '',
        quantity: '',
      },
    ]);
  }

  function formatBalanceAvailable(
    avail: { readonly numerator: string; readonly denominator?: string } | string | undefined,
  ): string {
    if (!avail) return '۰';
    if (typeof avail === 'string') {
      const parsed = Number(avail);
      return Number.isNaN(parsed) ? avail : parsed.toLocaleString('fa-IR');
    }
    const num = Number(avail.numerator);
    const den = Number(avail.denominator || '1');
    if (den === 1 || den === 0) return num.toLocaleString('fa-IR');
    const val = num / den;
    return (Number.isInteger(val) ? val : Number(val.toFixed(2))).toLocaleString('fa-IR');
  }

  function availableFor(allocation: AllocationDraft): string {
    const line = (order.status === 'ready' ? (order.data.lines ?? []) : []).find(
      (item) => item.lineId === allocation.lineId,
    );
    const productId = line ? productIdByRef.get(line.productRef) : undefined;
    const warehouseId = warehouseIdByCode.get(allocation.warehouseCode);
    if (!productId || !warehouseId || !allocation.locationId || stockBalances.status !== 'ready')
      return '—';
    const balance = stockBalances.data.balances.find(
      (b) =>
        b.productId === productId &&
        b.warehouseId === warehouseId &&
        b.warehouseLocationId === allocation.locationId,
    );
    return formatBalanceAvailable(balance?.available);
  }

  async function submitDeliver() {
    if (!selectedReservationId || !deliveryQuantity || !deliveryDate) return;
    setDeliverError(null);
    setDelivering(true);
    try {
      const result = await apiPost<{ movementId: string }>(
        `${base}/api/v1/sales/orders/${ref}/bridge/deliver`,
        {
          reservationId: selectedReservationId,
          quantityNumerator: deliveryQuantity,
          businessDate: deliveryDate,
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setLastMovementId(result.movementId);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setDeliverError(err instanceof ApiError ? err.envelope.error.message : 'تحویل ناموفق بود.');
    } finally {
      setDelivering(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="sk-breadcrumb">
        <span>فروش</span>
        <span>/</span>
        <Link to={`${base}/crm/orders`}>سفارش‌های فروش</Link>
        <span>/</span>
        <b className="ltr" style={{ fontFamily: 'var(--sk-font-mono)' }}>
          {ref}
        </b>
      </div>

      {order.status === 'loading' ? <LoadingState /> : null}
      {order.status === 'error' ? (
        <StatePanel
          variant={
            order.error.status === 403
              ? 'permission_denied'
              : order.error.status === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            order.error.status === 403
              ? 'دسترسی ندارید'
              : order.error.status === 404
                ? 'پیدا نشد'
                : 'قابل نمایش نیست'
          }
        />
      ) : null}

      {order.status === 'ready' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>سفارش فروش</h1>
            <V2Badge tone={STATUS_TONE[order.data.statusLabel] ?? 'neutral'}>
              {order.data.statusLabel}
            </V2Badge>
            <Link
              to={`${base}/crm/orders/${ref}/print`}
              className="sk-btn sk-btn--secondary sk-btn--sm"
              style={{ marginInlineStart: 'auto' }}
            >
              <Icon name="invoice" size="sm" />
              چاپ سفارش فروش
            </Link>
          </div>
          <V2Card>
            <div
              style={{
                padding: '16px 20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 16,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>طرف‌حساب</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  {order.data.partyRef ? (
                    <PartyLink base={base} partyRef={order.data.partyRef}>
                      {order.data.partyRef}
                    </PartyLink>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>پیش‌فاکتور</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{order.data.quoteRef ?? '—'}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>مبلغ کل</span>
                <span className="ltr sk-num" style={{ fontSize: 14, fontWeight: 500 }}>
                  {formatRialFa(order.data.totalRial, unit)} {label}
                </span>
              </div>
            </div>
          </V2Card>
          {order.data.lines && order.data.lines.length > 0 ? (
            <V2Card>
              <div className="sk-table-scroll">
                <table className="sk-table sk-table--compact">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>شرح کالا</th>
                      <th>کد کالا</th>
                      <th>مقدار</th>
                      <th>واحد</th>
                      <th>فی ({label})</th>
                      <th>تخفیف ({label})</th>
                      <th>مالیات</th>
                      <th>جمع ردیف ({label})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.data.lines.map((line) => (
                      <tr key={line.lineId}>
                        <td>{line.rowNumber.toLocaleString('fa-IR')}</td>
                        <td>{line.description}</td>
                        <td className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                          {line.productRef}
                        </td>
                        <td>{line.quantity}</td>
                        <td>{line.uomRef}</td>
                        <td className="ltr sk-num">{formatRialFa(line.unitPriceRial, unit)}</td>
                        <td className="ltr sk-num">{formatRialFa(line.discountRial, unit)}</td>
                        <td className="ltr sk-num">
                          {line.taxRef ?? '—'}
                          {Number(line.taxAmountRial) > 0
                            ? ` (${formatRialFa(line.taxAmountRial, unit)})`
                            : ''}
                        </td>
                        <td className="ltr sk-num">{formatRialFa(line.lineTotalRial, unit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </V2Card>
          ) : null}

          {actionError ? (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-danger-soft)',
                fontSize: 13,
                color: 'var(--sk-fg)',
              }}
            >
              {actionError}
            </div>
          ) : null}

          {order.data.status === 'draft' && order.data.actions?.canConfirm ? (
            <V2Card>
              <V2CardHeader title="تخصیص انبار پیش از تأیید" />
              <div
                style={{
                  padding: '0 20px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >
                <p style={{ margin: 0, color: 'var(--sk-fg-muted)', fontSize: 13 }}>
                  مجموع تخصیص هر ردیف باید دقیقاً برابر مقدار سفارش باشد. کمبود موجودی کل تأیید را
                  برمی‌گرداند.
                </p>
                {(order.data.lines ?? []).map((line) => {
                  const lineAllocations = allocations.filter((item) => item.lineId === line.lineId);
                  return (
                    <div
                      key={line.lineId}
                      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                    >
                      <strong>
                        ردیف {line.rowNumber.toLocaleString('fa-IR')} — {line.productRef} —{' '}
                        {line.quantity} {line.uomRef}
                      </strong>
                      {lineAllocations.map((allocation) => {
                        const warehouseId = warehouseIdByCode.get(allocation.warehouseCode);
                        const allowedLocations = warehouseId
                          ? locationOptions.filter(
                              (item) => String(item['warehouseId']) === warehouseId,
                            )
                          : [];
                        return (
                          <V2FormGrid key={allocation.key}>
                            <V2FormField label="انبار" width="medium">
                              <select
                                className="sk-select"
                                value={allocation.warehouseCode}
                                onChange={(event) =>
                                  updateAllocation(allocation.key, {
                                    warehouseCode: event.target.value,
                                    locationId: '',
                                    locationCode: '',
                                  })
                                }
                              >
                                <option value="">— انتخاب انبار —</option>
                                {warehouseOptions.map((warehouse) => (
                                  <option
                                    key={String(warehouse['code'])}
                                    value={String(warehouse['code'])}
                                  >
                                    {String(warehouse['name'] ?? warehouse['code'])}
                                  </option>
                                ))}
                              </select>
                            </V2FormField>
                            <V2FormField label="محل انبار" width="medium">
                              <select
                                className="sk-select"
                                value={allocation.locationId}
                                disabled={!allocation.warehouseCode}
                                onChange={(event) => {
                                  const locId = event.target.value;
                                  const loc = allowedLocations.find(
                                    (item) =>
                                      String(item['warehouseLocationId'] ?? item['id']) === locId,
                                  );
                                  updateAllocation(allocation.key, {
                                    locationId: locId,
                                    locationCode: loc ? String(loc['code'] ?? '') : '',
                                  });
                                }}
                              >
                                <option value="">— انتخاب محل —</option>
                                {allowedLocations.map((location) => {
                                  const locId = String(
                                    location['warehouseLocationId'] ?? location['id'],
                                  );
                                  return (
                                    <option key={locId} value={locId}>
                                      {String(location['name'] ?? location['code'])} (
                                      {String(location['code'])})
                                    </option>
                                  );
                                })}
                              </select>
                            </V2FormField>
                            <V2FormField label={`مقدار (${line.uomRef})`} width="short">
                              <input
                                className="sk-input ltr sk-num"
                                inputMode="numeric"
                                value={allocation.quantity}
                                onChange={(event) =>
                                  updateAllocation(allocation.key, { quantity: event.target.value })
                                }
                              />
                            </V2FormField>
                            <V2FormField label="موجودی قابل استفاده" width="short">
                              <span className="sk-num">{availableFor(allocation)}</span>
                            </V2FormField>
                            <V2Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setAllocations((current) =>
                                  current.filter((item) => item.key !== allocation.key),
                                )
                              }
                            >
                              حذف
                            </V2Button>
                          </V2FormGrid>
                        );
                      })}
                      <div>
                        <V2Button
                          variant="secondary"
                          size="sm"
                          onClick={() => addAllocation(line.lineId)}
                        >
                          افزودن تخصیص
                        </V2Button>
                      </div>
                    </div>
                  );
                })}
                <V2FormField label="تاریخ رزرو" width="medium">
                  <JalaliDateInput value={allocationDate} onChange={setAllocationDate} />
                </V2FormField>
              </div>
            </V2Card>
          ) : null}

          <div style={{ display: 'flex', gap: 8 }}>
            {order.data.actions?.canConfirm ? (
              <V2Button
                variant="primary"
                disabled={submitting || allocations.length === 0}
                onClick={() => setConfirming('confirm')}
              >
                تأیید سفارش
              </V2Button>
            ) : null}
            {order.data.actions?.canCancel ? (
              <V2Button
                variant="ghost"
                style={{ color: 'var(--sk-danger)' }}
                disabled={submitting}
                onClick={() => setConfirming('cancel')}
              >
                لغو سفارش
              </V2Button>
            ) : null}
          </div>

          {order.data.actions?.canDeliver ? (
            <V2Card>
              <V2CardHeader title="تحویل از رزرو فعال" />
              <div
                style={{
                  padding: '0 20px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <V2FormGrid>
                    <V2FormField label="رزرو فعال" width="wide">
                      <select
                        className="sk-select"
                        value={selectedReservationId}
                        onChange={(event) => setSelectedReservationId(event.target.value)}
                      >
                        <option value="">— انتخاب رزرو —</option>
                        {fulfillment.status === 'ready'
                          ? fulfillment.data.reservations
                              .filter(
                                (reservation) =>
                                  reservation.status === 'active' &&
                                  reservation.activeQuantity !== '0',
                              )
                              .map((reservation) => (
                                <option
                                  key={reservation.reservationId}
                                  value={reservation.reservationId}
                                >
                                  {reservation.warehouse.ref}/{reservation.location.ref} — مانده{' '}
                                  {reservation.activeQuantity}
                                </option>
                              ))
                          : null}
                      </select>
                    </V2FormField>
                    <V2FormField label="مقدار تحویل" width="short">
                      <input
                        className="sk-input ltr sk-num"
                        inputMode="numeric"
                        value={deliveryQuantity}
                        onChange={(event) => setDeliveryQuantity(event.target.value)}
                      />
                    </V2FormField>
                    <V2FormField label="تاریخ تحویل" width="medium">
                      <JalaliDateInput value={deliveryDate} onChange={setDeliveryDate} />
                    </V2FormField>
                  </V2FormGrid>
                  {deliverError ? (
                    <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>{deliverError}</span>
                  ) : null}
                  {lastMovementId ? (
                    <span style={{ fontSize: 12, color: 'var(--sk-success)' }}>
                      حواله ثبت شد: <span className="ltr">{lastMovementId}</span>
                    </span>
                  ) : null}
                  <div>
                    <V2Button
                      variant="secondary"
                      size="sm"
                      disabled={
                        !selectedReservationId || !deliveryQuantity || !deliveryDate || delivering
                      }
                      onClick={() => void submitDeliver()}
                    >
                      {delivering ? 'در حال تحویل…' : 'ثبت تحویل'}
                    </V2Button>
                  </div>
                </div>
              </div>
            </V2Card>
          ) : null}

          <RelatedOpenItemsCard kind="ar" sourceReference={order.data.ref} base={base} />

          {order.data.status === 'confirmed' ? (
            <V2Card>
              <V2CardHeader title="پروژه اجرایی مرتبط" />
              <div style={{ padding: '0 20px 20px' }}>
                {createdProject ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 13, color: 'var(--sk-color-text-muted, #555)' }}>
                      پروژه فعال:
                    </span>
                    <Link
                      to={`${base}/projects/detail/${createdProject.projectRef}`}
                      style={{ fontSize: 14, fontWeight: 600, color: 'var(--sk-primary, #2563eb)' }}
                    >
                      {createdProject.projectName} ({createdProject.projectRef})
                    </Link>
                  </div>
                ) : (
                  <div>
                    <p
                      style={{
                        margin: '0 0 12px',
                        fontSize: 13,
                        color: 'var(--sk-color-text-muted, #666)',
                      }}
                    >
                      برای پیگیری فرایند تحویل، نصب و هماهنگی‌های اجرای این سفارش می‌توانید پروژهٔ
                      متصل به سفارش ایجاد کنید.
                    </p>
                    {projectError ? (
                      <div
                        style={{
                          color: 'var(--sk-danger, #ef4444)',
                          fontSize: 13,
                          marginBottom: 12,
                        }}
                      >
                        {projectError}
                      </div>
                    ) : null}
                    <V2Button
                      variant="secondary"
                      size="sm"
                      disabled={creatingProject}
                      onClick={() => void handleCreateProject()}
                    >
                      {creatingProject ? 'در حال ایجاد پروژه…' : 'ایجاد پروژه اجرایی مرتبط'}
                    </V2Button>
                  </div>
                )}
              </div>
            </V2Card>
          ) : null}
        </>
      ) : null}

      {fulfillment.status === 'loading' ? <LoadingState /> : null}
      {fulfillment.status === 'error' ? (
        <StatePanel
          variant={fulfillment.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            fulfillment.error.status === 403
              ? 'دسترسی مشاهده وضعیت تأمین را ندارید'
              : 'وضعیت تأمین و تحویل قابل نمایش نیست'
          }
        />
      ) : null}
      {fulfillment.status === 'ready' ? (
        <>
          <V2Card>
            <div
              style={{
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <strong>وضعیت واقعی تأمین و تحویل</strong>
              <V2Badge tone={FULFILLMENT_TONE[fulfillment.data.fulfillmentStatus]}>
                {FULFILLMENT_LABEL[fulfillment.data.fulfillmentStatus]}
              </V2Badge>
              <span style={{ color: 'var(--sk-fg-muted)', fontSize: 13 }}>
                {fulfillment.data.fullyDeliveredLineCount.toLocaleString('fa-IR')} از{' '}
                {fulfillment.data.lineCount.toLocaleString('fa-IR')} ردیف کاملاً تحویل شده
              </span>
            </div>
          </V2Card>

          {fulfillment.data.lines.length === 0 ? (
            <V2EmptyState icon="cart" title="این سفارش ردیف کالا ندارد" compact />
          ) : (
            <V2Card>
              <div className="sk-table-scroll">
                <table className="sk-table sk-table--compact">
                  <thead>
                    <tr>
                      <th>کالا</th>
                      <th>واحد</th>
                      <th>سفارش</th>
                      <th>رزرو فعال</th>
                      <th>تحویل‌شده</th>
                      <th>مانده تحویل</th>
                      <th>وضعیت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fulfillment.data.lines.map((line) => (
                      <tr key={line.lineId}>
                        <td>
                          {line.productName}
                          <div
                            className="ltr"
                            style={{ color: 'var(--sk-fg-muted)', fontSize: 12 }}
                          >
                            {line.productRef}
                          </div>
                        </td>
                        <td>
                          {line.uomName} ({line.uomRef})
                        </td>
                        <td className="sk-num">{line.orderedQuantity}</td>
                        <td className="sk-num">{line.reservedQuantity}</td>
                        <td className="sk-num">{line.deliveredQuantity}</td>
                        <td className="sk-num">{line.remainingQuantity}</td>
                        <td>
                          <V2Badge tone={FULFILLMENT_TONE[line.status]}>
                            {FULFILLMENT_LABEL[line.status]}
                          </V2Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </V2Card>
          )}

          <V2Card>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--sk-divider)' }}>
              <strong>سوابق رزرو</strong>
            </div>
            {fulfillment.data.reservations.length === 0 ? (
              <div style={{ padding: 18, color: 'var(--sk-fg-muted)' }}>
                رزروی برای این سفارش ثبت نشده است.
              </div>
            ) : (
              <div className="sk-table-scroll">
                <table className="sk-table sk-table--compact">
                  <thead>
                    <tr>
                      <th>شناسه رزرو</th>
                      <th>انبار</th>
                      <th>محل</th>
                      <th>مقدار اولیه</th>
                      <th>رزرو فعال</th>
                      <th>تحویل‌شده</th>
                      <th>وضعیت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fulfillment.data.reservations.map((item) => (
                      <tr key={item.reservationId}>
                        <td className="ltr">{item.reservationId}</td>
                        <td>
                          {item.warehouse.name} ({item.warehouse.ref})
                        </td>
                        <td>
                          {item.location.name} ({item.location.ref})
                        </td>
                        <td className="sk-num">{item.quantity}</td>
                        <td className="sk-num">{item.activeQuantity}</td>
                        <td className="sk-num">{item.deliveredQuantity}</td>
                        <td>{item.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </V2Card>

          <V2Card>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--sk-divider)' }}>
              <strong>حواله‌های خروج مرتبط</strong>
            </div>
            {fulfillment.data.movements.length === 0 ? (
              <div style={{ padding: 18, color: 'var(--sk-fg-muted)' }}>
                حواله خروجی ثبت نشده است.
              </div>
            ) : (
              <div className="sk-table-scroll">
                <table className="sk-table sk-table--compact">
                  <thead>
                    <tr>
                      <th>شماره حواله</th>
                      <th>تاریخ</th>
                      <th>مقدار</th>
                      <th>جزئیات انبار</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fulfillment.data.movements.map((item) => (
                      <tr key={item.movementId}>
                        <td className="ltr">{item.movementNumber}</td>
                        <td className="ltr">{item.businessDate}</td>
                        <td className="sk-num">{item.quantity}</td>
                        <td>
                          <Link to={`${base}/inventory/movements/${item.movementId}`}>
                            مشاهده حواله
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </V2Card>

          <V2Card>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--sk-divider)' }}>
              <strong>handoff مالی</strong>
            </div>
            {fulfillment.data.financeHandoffs.length === 0 ? (
              <div style={{ padding: 18, color: 'var(--sk-fg-muted)' }}>
                هنوز خروج انباری برای تحویل به مالی ثبت نشده است.
              </div>
            ) : (
              <div style={{ padding: 18, display: 'grid', gap: 10 }}>
                {fulfillment.data.financeHandoffs.map((item) => (
                  <div
                    key={item.movementId}
                    style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <V2Badge
                      tone={item.status === 'cost_inventory_draft_created' ? 'success' : 'neutral'}
                    >
                      {item.label}
                    </V2Badge>
                    {item.journalNumber ? (
                      <span>
                        شماره پیش‌نویس: <b className="ltr">{item.journalNumber}</b>
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </V2Card>
        </>
      ) : null}

      {invoices.status === 'loading' ? <LoadingState /> : null}
      {invoices.status === 'error' ? (
        <ErrorState
          status={invoices.error.status}
          title={
            invoices.error.status === 403
              ? 'دسترسی مشاهدهٔ فاکتور و حساب دریافتنی را ندارید'
              : 'فاکتورهای فروش قابل نمایش نیستند'
          }
        />
      ) : null}
      {invoices.status === 'ready' ? (
        <V2Card>
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--sk-divider)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <strong>فاکتورهای فروش و ماندهٔ دریافتنی</strong>
            <V2Button variant="primary" size="sm" icon="invoice" onClick={() => setIssuing(true)}>
              صدور فاکتور
            </V2Button>
          </div>
          {invoices.data.invoices.length === 0 ? (
            <div style={{ padding: 18, color: 'var(--sk-fg-muted)' }}>
              برای تحویل‌های این سفارش هنوز فاکتور فروش صادر نشده است.
            </div>
          ) : (
            <div className="sk-table-scroll">
              <table className="sk-table sk-table--compact">
                <thead>
                  <tr>
                    <th>شماره فاکتور</th>
                    <th>تاریخ / سررسید</th>
                    <th>وضعیت</th>
                    <th>مبلغ کل</th>
                    <th>ماندهٔ دریافتنی</th>
                    <th>جزئیات</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.data.invoices.map((invoice) => (
                    <tr key={invoice.invoiceId}>
                      <td className="ltr">{invoice.officialNumber ?? invoice.invoiceId}</td>
                      <td>
                        <span className="ltr">{invoice.businessDate}</span>
                        <div className="ltr" style={{ color: 'var(--sk-fg-muted)', fontSize: 12 }}>
                          {invoice.dueDate}
                        </div>
                      </td>
                      <td>
                        <V2Badge tone={INVOICE_STATUS_TONE[invoice.status]}>
                          {INVOICE_STATUS_LABEL[invoice.status]}
                        </V2Badge>
                      </td>
                      <td className="sk-num">
                        {formatRialFa(invoice.totalAmountRial, unit)} {label}
                      </td>
                      <td className="sk-num">
                        {formatRialFa(invoice.openBalanceRial, unit)} {label}
                        <div style={{ color: 'var(--sk-fg-muted)', fontSize: 12 }}>
                          {invoice.receivableStatus}
                        </div>
                      </td>
                      <td>
                        <Link to={`${base}/crm/invoices/${invoice.invoiceId}`}>مشاهده فاکتور</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </V2Card>
      ) : null}

      {issuing ? (
        <InvoiceIssueDialog
          base={base}
          orderRef={ref}
          onClose={() => {
            setIssuing(false);
            if (searchParams.has('issue')) {
              const next = new URLSearchParams(searchParams);
              next.delete('issue');
              setSearchParams(next, { replace: true });
            }
          }}
          onCreated={() => setRefreshKey((k) => k + 1)}
        />
      ) : null}

      {confirming === 'confirm' ? (
        <V2ConfirmDialog
          open
          title="تأیید سفارش فروش؟"
          message="همهٔ تخصیص‌ها در یک تراکنش رزرو می‌شوند؛ کمبود موجودی کل تأیید را لغو می‌کند."
          confirmLabel={submitting ? 'در حال ثبت…' : 'تأیید'}
          cancelLabel="انصراف"
          onConfirm={() => void runAction('confirm')}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
      {confirming === 'cancel' ? (
        <V2ConfirmDialog
          open
          title="لغو سفارش فروش؟"
          message="این اقدام برگشت‌پذیر نیست و تمام ماندهٔ رزروهای فعال را آزاد می‌کند."
          danger
          confirmLabel={submitting ? 'در حال ثبت…' : 'لغو سفارش'}
          cancelLabel="انصراف"
          onConfirm={() => void runAction('cancel')}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </div>
  );
}
